import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { CiSearch } from "react-icons/ci";
import { MdPreview, MdCheck, MdClose, MdDateRange } from "react-icons/md";
import { FiFilter, FiX } from "react-icons/fi";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import { BsFillXCircleFill } from "react-icons/bs";
import ReportManageServices from "../../services/ReportManageServices";
import TransactionHistoryServices from "../../services/TransactionHistoryServices";
import { AuthContext } from "../../context/AuthContext";
import Select from "react-select";
import { selectCustomStyles } from "../../utils/utils";
import ReportDownload from "./components/ReportDownload";
import ReportDateDetailModal from "./components/ReportDateDetailModal";
import "../../styles/adminUsers.css";
import "../../styles/ReportManage.css";

const ReportManage = () => {
  const { user } = useContext(AuthContext);
  const reportManageApi = new ReportManageServices();
  const txApi = new TransactionHistoryServices();

  const [loading, setLoading] = useState(false);
  const [reportList, setReportList] = useState([]);
  const [userId, setUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);

  // Admin Users
  const [adminUsers, setAdminUsers] = useState([]);
  const [subAdminUsers, setSubAdminUsers] = useState([]);
  const [outletUsers, setOutletUsers] = useState([]);
  const [mobileUsers, setMobileUsers] = useState([]);

  // Search
  const [searchFilter, setSearchFilter] = useState("");
  // Pagination
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const defaultFrom = moment().startOf("month").format("YYYY-MM-DD");
  const defaultTo = moment().format("YYYY-MM-DD");
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);

  /**
   * Mobile-only bottom-sheet filter UI. Desktop CSS hides every mobile-only
   * element so the inline desktop header is preserved exactly as before.
   * Sheet renders its own copies of the filter inputs bound to the same state.
   */
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailTxnDate, setDetailTxnDate] = useState(null);

  const canViewAdminDateDetail =
    Number(user?.role_id) === 1 || (Number(user?.role_id) === 2 || Number(user?.role_id) === 5);
  const reportTargetUserId = userId || user?.user_id;

  const openDateDetail = useCallback((row) => {
    setDetailTxnDate(row.txn_date);
    setDetailModalOpen(true);
  }, []);

  const closeDateDetail = useCallback(() => {
    setDetailModalOpen(false);
    setDetailTxnDate(null);
  }, []);

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileFiltersOpen]);

  const datePresets = useMemo(
    () => [
      {
        key: "today",
        label: "Today",
        from: moment().format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
      },
      {
        key: "yesterday",
        label: "Yesterday",
        from: moment().subtract(1, "day").format("YYYY-MM-DD"),
        to: moment().subtract(1, "day").format("YYYY-MM-DD"),
      },
      {
        key: "7d",
        label: "Last 7 days",
        from: moment().subtract(6, "days").format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
      },
      {
        key: "this_month",
        label: "This month",
        from: moment().startOf("month").format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
      },
      {
        key: "last_month",
        label: "Last month",
        from: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        to: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      },
    ],
    [],
  );

  const activePresetKey = useMemo(() => {
    const match = datePresets.find(
      (p) => p.from === fromDate && p.to === toDate,
    );
    return match ? match.key : null;
  }, [datePresets, fromDate, toDate]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (fromDate && fromDate !== defaultFrom) n += 1;
    if (toDate && toDate !== defaultTo) n += 1;
    if (userId) n += 1;
    if (searchFilter) n += 1;
    return n;
  }, [fromDate, toDate, userId, searchFilter, defaultFrom, defaultTo]);

  const applyDatePreset = useCallback((preset) => {
    setFromDate(preset.from);
    setToDate(preset.to);
    setPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setUserId(null);
    setSelectedUserName(null);
    setSearchFilter("");
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    setPage(1);
  }, [defaultFrom, defaultTo]);

  const loadAdminUsers = async () => {
    const res = await txApi.getAdminUsers();
    if (res.isSuccess) {
      setAdminUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load admin users");
    }
  };
  const loadSubAdminUsers = async () => {
    const res = await txApi.getSubAdminUsers();
    if (res.isSuccess) {
      setSubAdminUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load sub admins");
    }
  };
  const loadOutletUsers = async () => {
    const res = await txApi.getOutletUsers();
    if (res.isSuccess) {
      setOutletUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load outlet users");
    }
  };

  const loadMobileUsers = async () => {
    const res = await txApi.getMobileUsers();
    if (res.isSuccess) {
      setMobileUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load mobile users");
    }
  };

  useEffect(() => {
    if (user.role_id === 1) {
      loadAdminUsers();
    }
    if (user.role_id === 1 || user.role_id === 2) {
      loadSubAdminUsers();
    }
    if ((user.role_id === 2 || user.role_id === 5) || user.role_id === 1) {
      loadOutletUsers();
    }
    if ((user.role_id === 2 || user.role_id === 5) || user.role_id === 1 || user.role_id === 3) {
      loadMobileUsers();
    }
  }, []);

  const getReportList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await reportManageApi.getReportList(
        fromDate,
        toDate,
        userId || undefined,
        page,
        limit,
        searchFilter,
      );
      if (response?.isSuccess) {
        setReportList(response.data || []);
        setPagination(response.pagination || null);
      }
    } catch (error) {
      console.error("Error in getReportList:", error);
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, userId, page, limit, searchFilter]);

  useEffect(() => {
    getReportList();
  }, [getReportList]);

  const onSearch = (e) => {
    setPage(1);
    setUserId(null);
    setSelectedUserName(null);
    setSearchFilter(e.target.value);
  };

  const onFrom = (e) => {
    setPage(1);
    setFromDate(e.target.value);
  };

  const onTo = (e) => {
    setPage(1);
    setToDate(e.target.value);
  };

  return (
    <div
      className={`report-wrap rm-page${mobileFiltersOpen ? " rm-filters-open" : ""}`}
    >
      <div className="report-header">
        <div className="report-header-top">
          <div className="report-filters gap-2">
            <div className="report-date">
              <label>From</label>
              <input type="date" value={fromDate} onChange={onFrom} />
            </div>
            <div className="report-date">
              <label>To</label>
              <input type="date" value={toDate} onChange={onTo} />
            </div>
          </div>
          <div className="d-flex justify-content-end align-items-center gap-1">
            <ReportDownload
              reportList={reportList}
              fromDate={fromDate}
              toDate={toDate}
              disabled={loading}
              userName={
                userId
                  ? [...adminUsers, ...subAdminUsers, ...outletUsers, ...mobileUsers].find(
                      (u) => Number(u.id) === Number(userId),
                    )?.name || user?.name
                  : user?.name || ""
              }
            />
            <button
              type="button"
              className="report-clear-button"
              onClick={() => {
                setUserId(null);
                setSelectedUserName(null);
                setSearchFilter("");
                setPage(1);
                setLimit(10);
                setFromDate(moment().startOf("month").format("YYYY-MM-DD"));
                setToDate(moment().format("YYYY-MM-DD"));
              }}
            >
              <BsFillXCircleFill size={18} className="report-clear-icon" />
              <span>Clear</span>
            </button>
          </div>
        </div>

        <div className="report-header-row">
          <div className="admin-search report-search">
            <div className="search-box" style={{ width: "100%" }}>
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search (name, game_id, mobile...)"
                value={searchFilter}
                onChange={onSearch}
              />
            </div>
          </div>
          {user.role_id === 1 && (
            <div className="report-select-wrap">
              <Select
                styles={selectCustomStyles}
                aria-labelledby="aria-label"
                inputId="admin-users-select"
                instanceId="admin-users-select"
                name="admin-users-select"
                isSearchable={true}
                openMenuOnFocus={true}
                filterOption={(option, inputValue) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                options={adminUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  userId
                    ? adminUsers
                        .map((u) => ({
                          value: Number(u.id),
                          label: u.username,
                        }))
                        .find((o) => o.value === Number(userId)) || null
                    : null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setSelectedUserName(opt?.label ?? null);
                  setPage(1);
                }}
                placeholder="Select Admin"
                isClearable={true}
              />
            </div>
          )}
          {(user.role_id === 1 || user.role_id === 2) && (
            <div className="report-select-wrap">
              <Select
                styles={selectCustomStyles}
                inputId="sub-admin-users-select"
                instanceId="sub-admin-users-select"
                name="sub-admin-users-select"
                isSearchable
                openMenuOnFocus
                options={subAdminUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  subAdminUsers
                    .map((u) => ({ value: Number(u.id), label: u.username }))
                    .find((o) => o.value === Number(userId)) || null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                setSelectedUserName(opt?.label ?? null);
                }}
                placeholder="Select Sub Admin"
                isClearable
              />
            </div>
          )}
          {(user.role_id === 1 || (user.role_id === 2 || user.role_id === 5)) && (
            <div className="report-select-wrap">
              <Select
                styles={selectCustomStyles}
                aria-labelledby="aria-label"
                inputId="outlet-users-select"
                instanceId="outlet-users-select"
                name="outlet-users-select"
                isSearchable={true}
                openMenuOnFocus={true}
                options={outletUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  outletUsers
                    .map((u) => ({ value: Number(u.id), label: u.username }))
                    .find((o) => o.value === Number(userId)) || null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                  setSelectedUserName(opt?.label ?? null);
                }}
                placeholder="Select Outlet"
                isClearable={true}
              />
            </div>
          )}
          {(user.role_id === 1 || (user.role_id === 2 || user.role_id === 5) || user.role_id === 3) && (
            <div className="report-select-wrap">
              <Select
                styles={selectCustomStyles}
                aria-labelledby="aria-label"
                inputId="mobile-users-select"
                instanceId="mobile-users-select"
                name="mobile-users-select"
                isSearchable={true}
                openMenuOnFocus={true}
                options={mobileUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  userId
                    ? mobileUsers
                        .map((u) => ({ value: Number(u.id), label: u.name }))
                        .find((o) => o.value === Number(userId)) || null
                    : null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                  setSelectedUserName(opt?.label ?? null);
                }}
                placeholder="Select Mobile"
                isClearable={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* ===== MOBILE ONLY — Compact toolbar (search + Filter pill) ===== */}
      <div className="rm-mobile-toolbar">
        <div className="rm-mobile-search">
          <div className="search-box">
            <CiSearch size={18} className="search-icon" />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search reports…"
              value={searchFilter}
              onChange={onSearch}
            />
          </div>
        </div>
        <button
          type="button"
          className="rm-mobile-filter-btn"
          onClick={() => setMobileFiltersOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={mobileFiltersOpen}
          aria-label="Open filters"
        >
          <FiFilter size={16} />
          {activeFilterCount > 0 && (
            <span className="rm-mfb-count">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* MOBILE ONLY — Active filter chips (one-tap remove) */}
      {activeFilterCount > 0 && (
        <div className="rm-active-chips">
          {(fromDate !== defaultFrom || toDate !== defaultTo) && (
            <span
              className={`rm-active-chip${activePresetKey ? " preset" : ""}`}
            >
              <span>
                {activePresetKey
                  ? datePresets.find((p) => p.key === activePresetKey)?.label
                  : `${moment(fromDate).format("DD MMM")} – ${moment(toDate).format("DD MMM")}`}
              </span>
              <button
                type="button"
                aria-label="Reset date range"
                onClick={() => {
                  setFromDate(defaultFrom);
                  setToDate(defaultTo);
                  setPage(1);
                }}
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {userId && (
            <span className="rm-active-chip user">
              <span>{selectedUserName || `User #${userId}`}</span>
              <button
                type="button"
                aria-label="Clear user"
                onClick={() => {
                  setUserId(null);
                  setSelectedUserName(null);
                  setPage(1);
                }}
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {searchFilter && (
            <span className="rm-active-chip">
              <span>“{searchFilter}”</span>
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearchFilter("");
                  setPage(1);
                }}
              >
                <FiX size={12} />
              </button>
            </span>
          )}
        </div>
      )}

      {/* ===== MOBILE ONLY — Bottom-sheet backdrop ===== */}
      <div
        className="rm-sheet-backdrop"
        onClick={() => setMobileFiltersOpen(false)}
        aria-hidden="true"
      />

      {/* ===== MOBILE ONLY — Bottom sheet ===== */}
      <aside
        className="rm-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Filter reports"
      >
        <div className="rm-sheet-grab" aria-hidden="true">
          <span />
        </div>
        <div className="rm-sheet-head">
          <div className="rm-sheet-head-left">
            <span className="rm-sheet-icon" aria-hidden="true">
              <FiFilter size={16} />
            </span>
            <div className="rm-sheet-titles">
              <h4 className="rm-sheet-title">
                Filters
                {activeFilterCount > 0 && (
                  <span className="rm-sheet-count">{activeFilterCount}</span>
                )}
              </h4>
              <p className="rm-sheet-subtitle">
                {activeFilterCount > 0
                  ? `${activeFilterCount} active filter${activeFilterCount > 1 ? "s" : ""}`
                  : "Refine your report"}
              </p>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              className="rm-sheet-reset"
              onClick={clearAllFilters}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            className="rm-sheet-close"
            onClick={() => setMobileFiltersOpen(false)}
            aria-label="Close filters"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="rm-sheet-body">
          {/* Quick presets */}
          <div className="rm-sheet-section">
            <div className="rm-sheet-section-label">Quick presets</div>
            <div className="rm-preset-row">
              {datePresets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`rm-preset-chip${activePresetKey === p.key ? " active" : ""}`}
                  onClick={() => applyDatePreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom date range */}
          <div className="rm-sheet-section">
            <div className="rm-sheet-section-label">Custom date range</div>
            <div className="rm-sheet-dates">
              <div className="rm-sheet-date">
                <label htmlFor="rm-from">From</label>
                <input
                  id="rm-from"
                  type="date"
                  value={fromDate}
                  onChange={onFrom}
                />
              </div>
              <div className="rm-sheet-date">
                <label htmlFor="rm-to">To</label>
                <input
                  id="rm-to"
                  type="date"
                  value={toDate}
                  onChange={onTo}
                />
              </div>
            </div>
          </div>

          {/* User selectors — only render those allowed for this role */}
          {(user.role_id === 1 ||
            (user.role_id === 2 || user.role_id === 5) ||
            user.role_id === 3) && (
            <div className="rm-sheet-section">
              <div className="rm-sheet-section-label">User</div>
              <div className="rm-sheet-user-selects">
                {user.role_id === 1 && (
                  <Select
                    styles={selectCustomStyles}
                    inputId="m-admin-users-select"
                    instanceId="m-admin-users-select"
                    name="m-admin-users-select"
                    isSearchable
                    openMenuOnFocus
                    options={adminUsers.map((u) => ({
                      value: Number(u.id),
                      label: u.username,
                    }))}
                    value={
                      userId
                        ? adminUsers
                            .map((u) => ({
                              value: Number(u.id),
                              label: u.username,
                            }))
                            .find((o) => o.value === Number(userId)) || null
                        : null
                    }
                    onChange={(opt) => {
                      setUserId(opt?.value ?? null);
                      setSelectedUserName(opt?.label ?? null);
                      setPage(1);
                    }}
                    placeholder="Select Admin"
                    isClearable
                  />
                )}
                {(user.role_id === 1 || user.role_id === 2) && (
                    <Select
                      styles={selectCustomStyles}
                      inputId="m-sub-admin-users-select"
                      instanceId="m-sub-admin-users-select"
                      name="m-sub-admin-users-select"
                      isSearchable
                      openMenuOnFocus
                      options={subAdminUsers.map((u) => ({
                        value: Number(u.id),
                        label: u.username,
                      }))}
                      value={
                        subAdminUsers
                          .map((u) => ({ value: Number(u.id), label: u.username }))
                          .find((o) => o.value === Number(userId)) || null
                      }
                      onChange={(opt) => {
                        setUserId(opt?.value ?? null);
                        setPage(1);
                      setSelectedUserName(opt?.label ?? null);
                      }}
                      placeholder="Select Sub Admin"
                      isClearable
                    />
                )}
                {(user.role_id === 1 || (user.role_id === 2 || user.role_id === 5)) && (
                  <Select
                    styles={selectCustomStyles}
                    inputId="m-outlet-users-select"
                    instanceId="m-outlet-users-select"
                    name="m-outlet-users-select"
                    isSearchable
                    openMenuOnFocus
                    options={outletUsers.map((u) => ({
                      value: Number(u.id),
                      label: u.username,
                    }))}
                    value={
                      outletUsers
                        .map((u) => ({
                          value: Number(u.id),
                          label: u.username,
                        }))
                        .find((o) => o.value === Number(userId)) || null
                    }
                    onChange={(opt) => {
                      setUserId(opt?.value ?? null);
                      setSelectedUserName(opt?.label ?? null);
                      setPage(1);
                    }}
                    placeholder="Select Outlet"
                    isClearable
                  />
                )}
                <Select
                  styles={selectCustomStyles}
                  inputId="m-mobile-users-select"
                  instanceId="m-mobile-users-select"
                  name="m-mobile-users-select"
                  isSearchable
                  openMenuOnFocus
                  options={mobileUsers.map((u) => ({
                    value: Number(u.id),
                    label: u.username,
                  }))}
                  value={
                    userId
                      ? mobileUsers
                          .map((u) => ({
                            value: Number(u.id),
                            label: u.username,
                          }))
                          .find((o) => o.value === Number(userId)) || null
                      : null
                  }
                  onChange={(opt) => {
                    setUserId(opt?.value ?? null);
                    setSelectedUserName(opt?.label ?? null);
                    setPage(1);
                  }}
                  placeholder="Select Mobile"
                  isClearable
                />
              </div>
            </div>
          )}
        </div>

        <div className="rm-sheet-foot">
          <button
            type="button"
            className="rm-sheet-btn ghost"
            onClick={clearAllFilters}
            disabled={activeFilterCount === 0}
          >
            <BsFillXCircleFill size={14} />
            <span>Clear all</span>
          </button>
          <button
            type="button"
            className="rm-sheet-btn primary"
            onClick={() => setMobileFiltersOpen(false)}
          >
            Done
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
      </aside>

      <div>
        {loading ? (
          <Loader message="Loading report list..." />
        ) : reportList.length > 0 ? (
          <div className="report-table-wrap">
            {reportList.map((row) => {
              return (
                <div key={row.txn_date}>
                  <div className="report-date-row">
                    <div className="report-date-row-left">
                      <span>
                        <MdDateRange size={18} />
                      </span>
                      <span className="pt-1">
                        {moment(row.txn_date).format("DD MMM YYYY")}
                      </span>
                      <span className="report-user-name">
                        [
                        {selectedUserName
                          ? `${selectedUserName}`
                          : user?.username}
                        ]
                      </span>
                    </div>
                    {canViewAdminDateDetail &&
                      (Number(row.user_role_id) === 2 || Number(row.user_role_id) === 5) && (
                        <button
                          type="button"
                          className="report-date-detail-btn"
                          title="View day detail"
                          onClick={() => openDateDetail(row)}
                          aria-label="View day detail"
                        >
                          <MdPreview size={20} />
                        </button>
                      )}
                  </div>
                  <div className="report-date-row-content-border">
                    <div className="report-date-row-content">
                      <div className="report-date-row-content-item1">
                        <div className="report-item1-label">Admin Receipt</div>
                        <div className="report-item1-value">
                          {row.admin_receipt_amount > 0 ? (
                            <span className="report-profit">
                              +
                              {Number(
                                row.admin_receipt_amount || 0,
                              ).toLocaleString()}
                            </span>
                          ) : (
                            0
                          )}
                        </div>
                      </div>
                      <div className="report-date-row-content-item2">
                        <div className="report-item1-label">Admin Payment</div>
                        <div className="report-item1-value">
                          {row.admin_payment_amount > 0 ? (
                            <span className="report-loss">
                              -
                              {Number(
                                row.admin_payment_amount || 0,
                              ).toLocaleString()}
                            </span>
                          ) : (
                            0
                          )}
                        </div>
                      </div>
                    </div>
                    {row.user_role_id !== 1 && (
                      <div className="report-date-row-content">
                        <div className="report-date-row-content-item1">
                          <div className="report-item1-label">
                            Super Admin Receipt
                          </div>
                          <div className="report-item1-value">
                            {row.super_admin_receipt_amount > 0 ? (
                              <span className="report-profit">
                                +
                                {Number(
                                  row.super_admin_receipt_amount || 0,
                                ).toLocaleString()}
                              </span>
                            ) : (
                              0
                            )}
                          </div>
                        </div>
                        <div className="report-date-row-content-item2">
                          <div className="report-item1-label">
                            Super Admin Payment
                          </div>
                          <div className="report-item1-value">
                            {row.super_admin_payment_amount > 0 ? (
                              <span className="report-loss">
                                -
                                {Number(
                                  row.super_admin_payment_amount || 0,
                                ).toLocaleString()}
                              </span>
                            ) : (
                              0
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="report-date-row-content">
                      <div className="report-date-row-content-item1">
                        <div className="report-item1-label">Outlet Receipt</div>
                        <div className="report-item1-value">
                          {row.outlet_receipt_amount > 0 ? (
                            <span className="report-profit">
                              +
                              {Number(
                                row.outlet_receipt_amount || 0,
                              ).toLocaleString()}
                            </span>
                          ) : (
                            0
                          )}
                        </div>
                      </div>
                      <div className="report-date-row-content-item2">
                        <div className="report-item1-label">Outlet Payment</div>
                        <div className="report-item1-value">
                          {row.outlet_payment_amount > 0 ? (
                            <span className="report-loss">
                              -
                              {Number(
                                row.outlet_payment_amount || 0,
                              ).toLocaleString()}
                            </span>
                          ) : (
                            0
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="report-date-row-content">
                      <div className="report-date-row-content-item1">
                        <div className="report-item1-label">Mobile Receipt</div>
                        <div className="report-item1-value">
                          {row.mobile_receipt_amount > 0 ? (
                            <span className="report-profit">
                              +
                              {Number(
                                row.mobile_receipt_amount || 0,
                              ).toLocaleString()}
                            </span>
                          ) : (
                            0
                          )}
                        </div>
                      </div>
                      <div className="report-date-row-content-item2">
                        <div className="report-item1-label">Mobile Payment</div>
                        <div className="report-item1-value">
                          {row.mobile_payment_amount > 0 ? (
                            <span className="report-loss">
                              -
                              {Number(
                                row.mobile_payment_amount || 0,
                              ).toLocaleString()}
                            </span>
                          ) : (
                            0
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {((row.user_role_id === 2 || row.user_role_id === 5) ||
                    row.user_role_id === 3 ||
                    row.user_role_id === 4) && (
                    <>
                      <div className="report-user-balance-row">
                        <div className="report-user-balance-row-item1">
                          <span className="report-item1-label">
                            Opening Balance:{" "}
                          </span>
                          <span className="report-item1-value">
                            {Number(
                              row.user_opening_balance || 0,
                            ).toLocaleString()}
                          </span>
                        </div>
                        <div className="report-user-balance-row-item2">
                          <span className="report-item1-label">
                            Closing Balance:{" "}
                          </span>
                          <span className="report-item1-value">
                            {Number(
                              row.user_closing_balance || 0,
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                  {((row.user_role_id === 2 || row.user_role_id === 5) ||
                    row.user_role_id === 3 ||
                    row.user_role_id === 4) && (
                    <>
                      {/* One block per game the row carries - the pair of
                          fixed 3min / 5min blocks hid every other game. */}
                      {(row.games || []).map((g) => {
                        const pl = Number(g.profit_loss ?? 0);
                        return (
                          <div
                            key={g.game_code}
                            className="report-game-row-content-item3"
                          >
                            <div className="report-game-name">
                              {g.game_name || g.game_code}
                            </div>
                            <div className="report-game-row1">
                              <div className="report-item1-label">Purchase</div>
                              <div className="report-item1-value">
                                {Number(g.total_purchase_amount || 0).toLocaleString()}
                              </div>
                            </div>
                            <div className="report-game-row2">
                              <div className="report-item1-label">Win</div>
                              <div className="report-item1-value">
                                {Number(g.total_win_amount || 0).toLocaleString()}
                              </div>
                            </div>
                            <div className="report-game-row3">
                              <div className="report-item1-label">Cancel</div>
                              <div className="report-item1-value">
                                {Number(g.total_cancel_amount || 0).toLocaleString()}
                              </div>
                            </div>
                            <div className="report-game-row4">
                              <div className="report-item1-label">P/L</div>
                              <div
                                className={`report-item1-value ${
                                  pl >= 0 ? "report-profit" : "report-loss"
                                }`}
                              >
                                {pl >= 0
                                  ? pl.toLocaleString()
                                  : `-${Math.abs(pl).toLocaleString()}`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="report-game-row-content-item3 report-total-row">
                        <div className="report-game-name">Total</div>
                        <div className="report-game-row1">
                          <div className="report-item1-label">Purchase</div>
                          <div className="report-item1-value">
                            {Number(
                              row.total_data?.total_purchase_amount || 0,
                            ).toLocaleString()}
                          </div>
                        </div>
                        <div className="report-game-row2">
                          <div className="report-item1-label">Win</div>
                          <div className="report-item1-value">
                            {Number(
                              row.total_data?.total_win_amount || 0,
                            ).toLocaleString()}
                          </div>
                        </div>
                        <div className="report-game-row3">
                          <div className="report-item1-label">Cancel</div>
                          <div className="report-item1-value">
                            {Number(
                              row.total_data?.total_cancel_amount || 0,
                            ).toLocaleString()}
                          </div>
                        </div>
                        <div className="report-game-row4">
                          <div className="report-item1-label">Overall P/L</div>
                          <div
                            className={`report-item1-value report-overall-pl ${
                              (row.total_data?.overall_profit_loss ?? 0) >= 0
                                ? "report-profit"
                                : "report-loss"
                            }`}
                          >
                            {(row.total_data?.overall_profit_loss ?? 0) >= 0
                              ? Number(
                                  row.total_data?.overall_profit_loss || 0,
                                ).toLocaleString()
                              : `-${Math.abs(Number(row.total_data?.overall_profit_loss || 0)).toLocaleString()}`}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <NoData
            message="No report found"
            subText="Try adjusting your date range or filters."
          />
        )}
        {pagination && pagination.totalPages > 1 && (
          <div className="modern-pagination">
            <div className="mp-limit">
              <span>Show</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
              </select>
            </div>

            <div className="mp-controls">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                Prev
              </button>

              <div className="mp-info">
                Page <strong>{pagination.page}</strong> of{" "}
                <strong>{pagination.totalPages}</strong>
              </div>

              <button
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>

            <div className="mp-jump">
              <select
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
              >
                {Array.from({ length: pagination.totalPages }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    Page {i + 1}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <ReportDateDetailModal
        show={detailModalOpen}
        onClose={closeDateDetail}
        txnDate={detailTxnDate}
        userId={reportTargetUserId}
        displayUserName={
          selectedUserName || user?.name || user?.username || ""
        }
        loggedInRoleId={user?.role_id}
      />
    </div>
  );
};

export default ReportManage;
